/**
 * animation.ts — small "game-feel" helpers: floating text, element pulses, and a
 * decaying screen-shake controller. All are DOM-only and degrade gracefully.
 */
import { el } from './dom';

/** Material-style ripple from a pointer position inside a button. */
export function ripple(host: HTMLElement, clientX: number, clientY: number): void {
  if (!host.getBoundingClientRect) return;
  const r = host.getBoundingClientRect();
  if (r.width === 0) return;
  const size = Math.max(r.width, r.height) * 1.4;
  const span = el('span', { class: 'ripple' });
  span.style.width = span.style.height = `${size}px`;
  span.style.left = `${clientX - r.left - size / 2}px`;
  span.style.top = `${clientY - r.top - size / 2}px`;
  host.appendChild(span);
  setTimeout(() => span.remove(), 600);
}

/** Spawn a floating, fading text label anchored to an element. */
export function floatText(
  anchor: HTMLElement,
  text: string,
  kind: 'gain' | 'spend' | 'crit' = 'gain',
): void {
  if (!anchor.isConnected) return;
  const f = el('span', { class: `float-text ${kind}`, text });
  // Slight horizontal jitter so rapid spawns fan out.
  f.style.setProperty('--dx', `${(Math.random() * 2 - 1) * 16}px`);
  anchor.appendChild(f);
  setTimeout(() => f.remove(), 900);
}

/** Briefly re-trigger a CSS animation class on an element. */
export function pulse(node: HTMLElement, cls = 'pulse-once'): void {
  node.classList.remove(cls);
  // Force reflow so the animation restarts.
  void node.offsetWidth;
  node.classList.add(cls);
}

/**
 * ShakeController — applies a decaying random translate to a root element.
 * Call `kick(intensity)` on impactful events; `apply()` once per frame.
 */
export class ShakeController {
  private trauma = 0;
  /** When true, shake is fully suppressed (accessibility). */
  reducedMotion = false;

  constructor(private root: HTMLElement) {}

  kick(intensity = 0.6): void {
    if (this.reducedMotion) return;
    this.trauma = Math.min(1, this.trauma + intensity);
  }

  /** Advance and write the transform. `dt` in seconds. */
  apply(dt: number): void {
    if (this.reducedMotion || this.trauma <= 0) {
      this.trauma = 0;
      this.root.style.transform = '';
      return;
    }
    const amount = this.trauma * this.trauma; // quadratic falloff feels better
    const max = 14 * amount;
    const x = (Math.random() * 2 - 1) * max;
    const y = (Math.random() * 2 - 1) * max;
    const r = (Math.random() * 2 - 1) * 1.2 * amount;
    this.root.style.transform = `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) rotate(${r.toFixed(3)}deg)`;
    this.trauma = Math.max(0, this.trauma - dt * 1.8);
    if (this.trauma <= 0) this.root.style.transform = '';
  }
}
